/* Matching a Member Hours entry to a client.
   =========================================

   Member Hours rows were never linked to client records. A row carries a
   free-text `name` and an optional four-character `cin`, typed by hand into a
   table while someone works through twenty of them. The client profile now has
   to answer "how much time has been logged with this person", which means
   deciding, after the fact, which rows are about them.

   That is a guess, and the numbers it produces end up on invoices. So the
   rules here are deliberately conservative and the RESULT SAYS HOW IT WAS
   REACHED — 'id', 'cin' or 'name' — because a name match and an id match do
   not deserve the same confidence and the UI should not pretend otherwise.

   THE ORDER MATTERS, and each step can refuse rather than fall through:

   1. clientId — an explicit link, written by the picker on new rows. If a row
      has one and it points elsewhere, that is an ANSWER: this row belongs to
      another client. Falling through to name here would let a mistyped name
      re-attach a row that was deliberately assigned.

   2. CIN, when BOTH sides have one. Same logic: two different CINs mean two
      different people, so a mismatch stops rather than trying the name. This
      is the whole reason a CIN exists.

   3. Normalised name, only when the CIN comparison could not be made. The
      weakest link and the one that silently misses — the September export has
      "Alicia Carill0" with a digit zero, which will never equal "Alicia
      Carillo". Rows that match nothing are counted and shown, so a short
      total is visible rather than merely wrong.

   Nothing here reads the DOM, Firestore or any global, which is what makes it
   testable — and this is exactly the kind of logic where "it looked right when
   I opened the app" proves nothing. */

import { normName } from './text.js';

/* Upper-cased, stripped of anything that is not a letter or digit. CINs get
   typed with stray spaces and the field is only four characters, so " 782c "
   and "782C" have to be the same thing. */
export function normCin(s) {
  return (s || '').toString().toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/* Returns 'id' | 'cin' | 'name' | null. Null means "not this client",
   including the two cases where an identifier positively disagreed. */
export function mhMatchKind(entry, client) {
  if (!entry || !client) return null;

  // 1. An explicit link decides it, either way.
  if (entry.clientId) return (client.id && entry.clientId === client.id) ? 'id' : null;

  // 2. A CIN on both sides decides it, either way.
  const ec = normCin(entry.cin), cc = normCin(client.cin);
  if (ec && cc) return ec === cc ? 'cin' : null;

  // 3. Otherwise the name is all there is.
  const en = normName(entry.name), cn = normName(client.name);
  if (en && cn && en === cn) return 'name';
  return null;
}

export function mhMatchesClient(entry, client) {
  return mhMatchKind(entry, client) !== null;
}

/* Totals over already-matched entries. Kept beside the matcher because the
   figures it produces are the ones shown as a client's logged time, and both
   halves of that claim should be testable together. */
export function mhSumEntries(entries) {
  const list = Array.isArray(entries) ? entries : [];
  return {
    count: list.length,
    minutes: list.reduce((s, e) => s + (Number(e && e.totalMinutes) || 0), 0),
    units: list.reduce((s, e) => s + (Number(e && e.units) || 0), 0)
  };
}

/* ---------------------------------------------------------------------------
   MONTHS THAT HAVE ENTRIES

   The profile offers two views — everything, or one month — with arrows to
   step between months. Stepping through the CALENDAR would walk into empty
   months and, for a client with years of history, mean a lot of clicking past
   nothing. So the stepper walks the months that actually contain entries:
   every click lands somewhere with something in it, and the ends of the list
   bound the control without needing a separate range check.

   Dates are 'YYYY-MM-DD' and are SLICED, never parsed. 'YYYY-MM-DD' parses as
   UTC, so new Date('2026-01-01') is 31 December 2025 in any negative-offset
   timezone and January's entries would file under the wrong month. Slicing a
   string cannot drift.
--------------------------------------------------------------------------- */

/* Newest first, as { key: 'YYYY-MM', year, month } where month is 0-11.
   The key is what callers store and compare; year/month are for formatting. */
export function mhMonthsPresent(entries) {
  const keys = new Set();
  (Array.isArray(entries) ? entries : []).forEach(e => {
    const d = e && e.date;
    if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) keys.add(d.slice(0, 7));
  });
  return [...keys].sort().reverse().map(key => ({
    key,
    year: Number(key.slice(0, 4)),
    month: Number(key.slice(5, 7)) - 1
  }));
}

/* Entries in one 'YYYY-MM'. A falsy key means everything, which is what the
   All view passes rather than having its own branch. */
export function mhFilterMonth(entries, monthKey) {
  const list = Array.isArray(entries) ? entries : [];
  if (!monthKey) return list.slice();
  return list.filter(e => typeof (e && e.date) === 'string' && e.date.slice(0, 7) === monthKey);
}
